<template>
  <layout>
    <page-header :actions="actions">
      <template slot="header">
        <h4>
          {{ $t('bookings.edit') }}
          <small v-if="booking.number">#{{ booking.number }}</small>
        </h4>
        <div v-if="hasRelatedBooking">
          <button
            class="btn btn-link"
            type="button"
            @click="
              editRelatedBooking(returnBooking ? returnBooking : mainBooking)
            "
          >
            <i class="icon-enter5"></i>
            {{
              isReturnBooking
                ? $t('bookings.edit_main')
                : $t('bookings.edit_return')
            }}
          </button>
        </div>
        <button
          v-for="companion in companions"
          :key="companion.id"
          class="btn btn-link"
          type="button"
          @click="editRelatedBooking(companion)"
        >
          <i class="icon-enter5"></i>
          {{ $t('bookings.booking_number', { number: companion.number }) }}
        </button>
      </template>
    </page-header>
    <div class="content">
      <b-alert v-if="isReturnBooking" show>
        {{ $t('bookings.editing_return') }}
      </b-alert>
      <div v-if="currentDivision">
        <History v-if="view === 'history'" :id="$route.params.id" />
        <resource-form
          v-if="view === 'form'"
          ref="form"
          :key="$route.params.id"
          :id="$route.params.id"
          @saved="confirmChanges"
          @cancelled="returnBack"
          @loaded="formLoaded"
          @assigned="returnBack"
          @rejected="returnBack"
        />
      </div>
    </div>
  </layout>
</template>

<script>
import ResourceForm from './Form.vue'
import History from './History'
import BookingsApi from './Api'

const resourceBase = 'bookings'

export default {
  name: 'BookingEdit',

  components: {
    History,
    ResourceForm,
  },

  data() {
    return {
      booking: {},
      isReturnBooking: false,
      // Return booking in case editing booking with return
      returnBooking: null,
      // Main booking in case editing return booking
      mainBooking: null,
      companions: [],
      view: 'form',
    }
  },

  computed: {
    hasRelatedBooking() {
      return this.mainBooking || this.returnBooking
    },
    actions() {
      return this.$allows(`administer`)
        ? [
            {
              id: 'edit',
              text: this.$t('bookings.edit'),
              icon: 'icon-file-form',
              onClick: () => {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.view = 'form'
              },
            },
            {
              id: 'history',
              text: this.$t('bookings.history'),
              icon: 'icon-file-history',
              onClick: () => {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.view = 'history'
              },
            },
          ]
        : []
    },
  },

  methods: {
    api() {
      return new BookingsApi()
    },
    formLoaded(result) {
      this.booking = result.data
      this.isReturnBooking = result.data.is_return
      this.returnBooking = result.return_booking
      this.companions = result.companions
      this.mainBooking = result.main_booking

      if (typeof this.booking.company_id !== 'undefined')
        this.$refs.form.fields.get('driver_id').source.params[
          'where[company_id]'
        ] = this.booking.company_id
      this.$refs.form.fields.get('driver_id').source.params[
        'division_id'
      ] = this.booking.division_id
    },

    returnBack() {
      if (['processing', 'complete'].indexOf(this.booking.status) !== -1) {
        this.$router.push({ name: `${resourceBase}.tasks` })
        return
      }

      this.$router.push({ name: `${resourceBase}` })
    },

    editRelatedBooking(booking) {
      if (
        booking.status === 'cancelled' &&
        this.booking.status !== 'cancelled'
      ) {
        this.$alert.info(
          this.$t('bookings.messages.cancelled_editing_unavailable', {
            number: booking.number,
          }),
        )
        return
      }

      this.$router.push({
        name: `${resourceBase}.edit`,
        params: { id: booking.id },
      })
    },

    confirmChanges(data) {
      if (data.changes_id !== null) {
        this.askConfirmation(data)
        return
      }
      this.returnBack()
      this.$toast.success(data.message)
    },

    async askConfirmation(data) {
      const isRefund = data.amount_diff < 0
      let answer = await this.questionAlert(
        data.amount_diff,
        data.currency,
        isRefund,
        this.preconfirmCallback(data),
      )

      if (answer.value !== true) {
        this.api().clearPendingChanges(data.changes_id)
      }
    },

    questionAlert(diff, currency, isRefund, preConfirm) {
      const questionText = this.questionText(isRefund, diff, currency)
      return this.$alert.question(questionText.title, questionText.text, {
        showCancelButton: true,
        showLoaderOnConfirm: true,
        allowOutsideClick: false,
        confirmButtonText: this.$t(isRefund ? 'general.refund' : 'general.pay'),
        preConfirm,
      })
    },
    questionText(isRefund, diff, currency) {
      return {
        title: this.$t(
          isRefund
            ? 'bookings.messages.new_price_is_less'
            : 'bookings.messages.new_price_is_greater',
        ),
        text:
          this.$t(
            isRefund
              ? 'bookings.messages.do_you_want_refund_diff'
              : 'bookings.messages.do_you_want_request_invoice',
          ) +
          ` (${this.$n(diff, {
            style: 'currency',
            currency: currency.value,
          })})`,
      }
    },

    preconfirmCallback(data) {
      return () => {
        return this.api()
          .createPendingChangesInvoice(data.changes_id)
          .then(({ message }) => {
            this.$toast.success(message)
            this.returnBack()
          })
      }
    },
  },
}
</script>

<style>
.media img {
  width: 100px;
}
</style>
